import { useRef, useEffect, useCallback } from 'react';
import { useMatrixClient } from '../context/MatrixContext';
import { useSettings } from '../context/SettingsContext';

/**
 * Hook for managing outgoing typing indicators
 *
 * Handles the logic for sending typing=true/false events to the Matrix server
 * with proper timing and cleanup. Respects user's privacy settings.
 *
 * Re-sends typing events every 5 seconds to prevent the server-side timeout
 * (10 seconds per Matrix spec) from expiring while the user continues typing.
 *
 * @param {string} roomId - The room ID to send typing indicators to
 * @returns {object} Object with typing control functions
 */
export const useSendTypingIndicator = (roomId) => {
  const client = useMatrixClient();
  const { settings } = useSettings();
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  // Match Element Web and Matrix spec: 10 seconds server timeout
  const TYPING_TIMEOUT_MS = 10000;
  // Re-send typing events every 5 seconds to keep indicator alive
  const TYPING_REFRESH_MS = 5000;

  // Internal function to send typing event
  const sendTypingEvent = useCallback(() => {
    if (!settings.sendTypingIndicators) return;
    if (!client || !roomId) return;

    client.sendTyping(roomId, true, TYPING_TIMEOUT_MS)
      .catch((error) => {
        // On error, stop the refresh interval and reset state
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
        isTypingRef.current = false;

        // Silently ignore "not in room" errors - user may have left
        if (error.errcode !== 'M_FORBIDDEN' && error.httpStatus !== 403) {
          // Typing indicators are not critical, silently ignore errors
        }
      });
  }, [client, roomId, settings.sendTypingIndicators]);

  const onTypingStart = useCallback(() => {
    // Don't send if typing indicators are disabled in settings
    if (!settings.sendTypingIndicators) return;
    if (!client || !roomId) return;

    // Only send if we're not already typing
    if (!isTypingRef.current) {
      isTypingRef.current = true;

      // Send initial typing event
      sendTypingEvent();

      // Start interval to re-send typing events every 5 seconds
      // This prevents the server-side timeout from expiring while user keeps typing
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      refreshIntervalRef.current = setInterval(() => {
        if (isTypingRef.current) {
          sendTypingEvent();
        }
      }, TYPING_REFRESH_MS);
    }
  }, [client, roomId, settings.sendTypingIndicators, sendTypingEvent]);

  const onTypingStop = useCallback(() => {
    // Don't send if typing indicators are disabled in settings
    if (!settings.sendTypingIndicators) return;
    if (!client || !roomId || !isTypingRef.current) return;

    // Clear the refresh interval first
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    isTypingRef.current = false;

    // Send typing=false and handle Promise rejection with .catch()
    client.sendTyping(roomId, false)
      .catch(() => {
        // Silently ignore all errors - typing indicators are not critical
        // User may have left the room or lost connection
      });
  }, [client, roomId, settings.sendTypingIndicators]);

  const onTextChange = useCallback((newText) => {
    // Defensive guard: ensure newText is a string
    // This prevents crashes if non-string content (e.g., JSX objects) is accidentally passed
    if (typeof newText !== 'string') {
      // Silently ignore non-string input
      return;
    }
    
    if (newText.trim()) {
      // User is typing - only send start event if not already typing
      if (!isTypingRef.current) {
        onTypingStart();
      }
      
      // Reset the stop typing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Auto-stop typing after inactivity (matches server timeout)
      typingTimeoutRef.current = setTimeout(() => {
        onTypingStop();
      }, TYPING_TIMEOUT_MS);
    } else {
      // User cleared the input - immediate stop
      onTypingStop();

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  }, [onTypingStart, onTypingStop]);

  const onMessageSent = useCallback(() => {
    // Stop typing indicator when sending message
    onTypingStop();

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [onTypingStop]);

  // Cleanup typing state on unmount or room change
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }

      if (isTypingRef.current) {
        onTypingStop();
      }
    };
  }, [roomId, onTypingStop]);

  return {
    onTextChange,
    onMessageSent,
    onTypingStart,
    onTypingStop,
  };
};

export default useSendTypingIndicator;